import { readableUUID } from './readableUUID';
import { telemetry } from '../telemetry';

/**
 * Generic utilities for handling crmOrgId in URLs regardless of position
 * This supports both patterns:
 * - /automations/crmOrgId
 * - /routing/crmOrgId/tab/groups
 */

/**
 * Removes a specific crmOrgId from the URL path, regardless of its position
 * @param pathname Current URL pathname
 * @param crmOrgIdToRemove The specific crmOrgId to remove
 * @returns New pathname with the crmOrgId removed
 */
export const removeOrgFromPath = (pathname: string, crmOrgIdToRemove: string): string => {
  if (!crmOrgIdToRemove) return pathname;

  // Split path into segments
  const segments = pathname.split('/').filter(Boolean);

  // Remove the specific crmOrgId segment
  const filteredSegments = segments.filter((segment) => segment !== crmOrgIdToRemove);

  // Reconstruct path
  return '/' + filteredSegments.join('/');
};

/**
 * Replaces a specific crmOrgId in the URL path with a new one, regardless of position
 * @param pathname Current URL pathname
 * @param oldCrmOrgId The crmOrgId to replace (readable UUID format from URL)
 * @param newCrmOrgId The new crmOrgId to use (full UUID - will be converted to readable)
 * @returns New pathname with the crmOrgId replaced
 *
 * Examples:
 * - replaceOrgInPath('/automations/abc123', 'abc123', 'full-uuid-here') → '/automations/def456'
 * - replaceOrgInPath('/routing/abc123/tab/groups', 'abc123', 'full-uuid-here') → '/routing/def456/tab/groups'
 */
export const replaceOrgInPath = (
  pathname: string,
  oldCrmOrgId: string,
  newCrmOrgId: string,
  accountId: string,
): string => {
  if (!oldCrmOrgId || !newCrmOrgId || !accountId) {
    return pathname;
  }

  // Convert the new UUID to readable format for URL
  const newReadableUUID = readableUUID(newCrmOrgId);
  if (!newReadableUUID) {
    telemetry.captureError(
      new Error('replaceOrgInPath: Failed to convert new UUID to readable format'),
      {
        newCrmOrgId,
      },
    );
    return pathname;
  }

  const readableAccountId = readableUUID(accountId);
  if (!readableAccountId) {
    telemetry.captureError(
      new Error('replaceOrgInPath: Failed to convert accountId to readable format'),
      {
        accountId,
      },
    );
    return pathname;
  }

  const segments = pathname.split('/').filter(Boolean);
  const oldCrmOrgIdIndex = segments.indexOf(oldCrmOrgId);
  segments[oldCrmOrgIdIndex] = newReadableUUID;
  segments[oldCrmOrgIdIndex + 1] = readableAccountId;
  const updatedSegments = segments.map((segment) =>
    segment === oldCrmOrgId ? newReadableUUID : segment,
  );

  return '/' + updatedSegments.join('/');
};

/**
 * Replaces accountId in URL path while keeping crmOrgId
 * @param pathname Current URL pathname
 * @param newAccountId The new accountId to use (full UUID - will be converted to readable)
 * @param oldAccountId The accountId to replace (readable UUID format from URL)
 * @returns New pathname with the accountId replaced
 *
 * Examples:
 * - replaceAccountIdInPath('/alerts/abc123/old456', 'new-uuid', 'old456') → '/alerts/abc123/new789'
 * - replaceAccountIdInPath('/agentic/abc123/old456/agent/xyz', 'new-uuid', 'old456') → '/agentic/abc123/new789/agent/xyz'
 */
export const replaceAccountIdInPath = (
  pathname: string,
  newAccountId: string,
  oldAccountId: string,
): string => {
  if (!oldAccountId || !newAccountId) {
    return pathname;
  }

  // Convert the new UUID to readable format for URL
  const newReadableAccountId = readableUUID(newAccountId);
  if (!newReadableAccountId) {
    telemetry.captureError(
      new Error('replaceAccountIdInPath: Failed to convert new accountId to readable format'),
      {
        newAccountId,
      },
    );
    return pathname;
  }

  const segments = pathname.split('/').filter(Boolean);
  const oldAccountIdIndex = segments.indexOf(oldAccountId);

  if (oldAccountIdIndex === -1) {
    telemetry.captureError(
      new Error('replaceAccountIdInPath: oldAccountId not found in pathname'),
      {
        pathname,
        oldAccountId,
      },
    );
    return pathname;
  }

  segments[oldAccountIdIndex] = newReadableAccountId;
  return '/' + segments.join('/');
};

/**
 * Adds a crmOrgId to a URL path after the first segment
 * @param pathname Current URL pathname (e.g., '/agentic/agent/123/chat/456')
 * @param crmOrgId The crmOrgId to add (full UUID - will be converted to readable)
 * @returns New pathname with the crmOrgId inserted (e.g., '/agentic/abc123/agent/123/chat/456')
 *
 * Examples:
 * - addCrmOrgIdToPath('/agentic/agent/123', 'account-id', 'full-uuid-here') → '/agentic/abc123/account-id/agent/123'
 * - addCrmOrgIdToPath('/agentic/agent/123/chat/456', 'account-id', 'full-uuid-here') → '/agentic/abc123/account-id/agent/123/chat/456'
 */
export const addCrmOrgIdToPath = (
  pathname: string,
  accountId: string,
  crmOrgId?: string,
): string | undefined => {
  if (!crmOrgId || !accountId) {
    return;
  }
  const readableCrmOrgId = readableUUID(crmOrgId);
  const readableAccountId = readableUUID(accountId);
  if (!readableCrmOrgId || !readableAccountId) {
    return;
  }

  // pathname starts with '/'
  const path = pathname.split('/').filter(Boolean);
  const [base, ...rest] = path;
  const result = '/' + [base, readableCrmOrgId, readableAccountId, ...rest].join('/');

  return result;
};
